import React, { useCallback, useState, ElementType, forwardRef } from 'react';
import { Box, BoxProps, useScrollTrigger, Slide, Container, useTheme } from '@mui/material';

export interface PageLayoutProps extends BoxProps<ElementType> {
  /**
   * React node that renders layout header
   */
  header?: React.ReactNode;
  /**
   * React node that renders layout footer
   */
  footer?: React.ReactNode;
  /**
   * Specifies position of header
   * - static: header is fixed on top of the content
   * - fixed: header is visible while user scrolls the content
   * - slide: header is visible until user scrolls down beyond `headerSlideThreshold`, header will reappear when user scrolls up
   * @default static
   */
  headerPosition?: 'static' | 'fixed' | 'slide';
  /**
   * Specifies threshold that is used to hide header for the headerPosition="slide"
   * @default 250
   */
  headerSlideThreshold?: number;
  /**
   * Specifies if content should be automatically wrapped into container component
   * @default false
   */
  wrapContent?: boolean;
  /**
   * Properties applied to header wrapper
   */
  headerProps?: BoxProps<ElementType>;
  /**
   * Properties applied to header wrapper
   */
  headerContainerProps?: BoxProps<ElementType>;
  /**
   * Properties applied to root header wrapper
   */
  headerWrapperProps?: BoxProps<ElementType>;
  /**
   * Properties applied to page content wrapper
   */
  contentProps?: BoxProps<ElementType>;
  /**
   * Properties applied to footer wrapper
   */
  footerProps?: BoxProps<ElementType>;
}

export const PageLayout: React.FC<PageLayoutProps> = forwardRef(
  (
    {
      children,
      sx,
      header,
      footer,
      headerPosition = 'static',
      headerSlideThreshold = 250,
      wrapContent = false,
      headerProps,
      headerContainerProps,
      headerWrapperProps,
      contentProps,
      footerProps,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const { sx: headerWrapperSx, ...restHeaderWrapper } = headerWrapperProps ?? {};
    const {
      sx: headerContainerSx,
      className: headerContainerClassName,
      ...restHeaderContainer
    } = headerContainerProps ?? {};
    // header height to take actual space in dom for the fixed/slide variants
    const [headerHeight, setHeaderHeight] = useState<number>(Number.parseFloat(String(theme.mixins.toolbar.minHeight)));
    const headerContainerRef = useCallback(
      (element: HTMLElement) => {
        if (headerPosition === 'static' || !element) {
          return;
        }
        new ResizeObserver(() => {
          setHeaderHeight(element.offsetHeight);
        }).observe(element);
      },
      [headerPosition !== 'static'],
    );
    // trigger hide of the header during scroll
    const slideTrigger = useScrollTrigger({
      target: window,
      threshold: headerSlideThreshold,
    });

    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          backgroundColor: 'surface.primary',
          display: 'grid',
          gridTemplateRows: [header && 'max-content', 'auto', footer && 'max-content'].filter(Boolean).join(' '),
          ...sx,
        }}
        data-testid="PageLayout"
        ref={ref}
        {...rest}
      >
        {header && (
          <Box
            sx={{
              ...(headerPosition !== 'static' && headerHeight && { height: headerHeight }),
              ...headerWrapperSx,
            }}
            data-testid="PageLayout.headerWrapper"
            {...restHeaderWrapper}
          >
            <Box
              {...(headerPosition === 'slide' && {
                component: Slide,
                appear: false,
                direction: 'down',
                in: !slideTrigger,
              })}
              sx={{
                ...(headerPosition !== 'static' && {
                  position: 'fixed',
                  top: '0',
                  left: '0',
                  right: '0',
                  zIndex: theme.zIndex.appBar,
                }),
                ...headerContainerSx,
              }}
              className={[headerPosition !== 'static' ? 'mui-fixed' : '', headerContainerClassName]
                .filter(Boolean)
                .join(' ')}
              {...restHeaderContainer}
            >
              <Box ref={headerContainerRef} data-testid="PageLayout.header" {...headerProps}>
                {header}
              </Box>
            </Box>
          </Box>
        )}
        <Box {...(wrapContent && { component: Container, maxWidth: 'xl' })} data-testid="Container" {...contentProps}>
          {children}
        </Box>
        {footer && (
          <Box data-testid="PageLayout.footer" {...footerProps}>
            {footer}
          </Box>
        )}
      </Box>
    );
  },
);
